import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { MdRefresh, MdExpandMore, MdChevronRight, MdClose, MdOpenInNew, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import { atlasService } from '../../services/atlasService';
import { mergeLayout, PLOTLY_CONFIG } from '../../components/charts/chartTheme';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';

// Macro Regime — Layer 2. The reality-anchored regime read: 5 axes (Growth/Inflation/Rates/Liquidity/Risk),
// each a hierarchy of researched concept groups → member series. Every number carries the REALITY TRIPLE —
// the actual value, its percentile-vs-10y, and a robust (median/MAD) z — plus a plain-English stance word.
// Colour follows ECONOMIC meaning (good_up), not the raw sign of z: a +0.7 on Rates is "Restrictive" → a
// headwind → red, even though the z is positive. Drill axis → group → series → the single-source-of-truth
// history chart + FRED definition. Backend: apps/trading/services/macro_regime.py (regime_payload).

const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
const GOLD = 'rgb(var(--brand-gold-rgb))';
const UP = '#10b981';
const DOWN = '#ef4444';
const AMBER = '#f59e0b';

// economic tone: is this reading good or bad for the economy? good_up axes (growth/liquidity) are good when
// z>0; the rest (inflation/rates/risk) are bad when z>0. Near-zero = neutral gray.
function tone(goodUp, z) {
  if (z == null || Math.abs(z) < 0.25) return '#9ca3af';
  const good = goodUp ? z > 0 : z < 0;
  return good ? UP : DOWN;
}
const fmtZ = (z) => (z == null ? '—' : `${z >= 0 ? '+' : ''}${z.toFixed(2)}`);
const fmtPct = (p) => (p == null ? '—' : `${Math.round(p)}`);
const fmtVal = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
  if (a >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return v.toFixed(a < 10 ? 2 : 1);
};

const AXIS_META = {
  growth: { label: 'Growth', blurb: 'Real activity — labor, output, consumption, orders, housing' },
  inflation: { label: 'Inflation', blurb: 'Price pressure — PCE / CPI, expectations, PPI, wages' },
  rates: { label: 'Rates', blurb: 'Policy & curve — front-end, slope, Treasury, corporate' },
  liquidity: { label: 'Liquidity', blurb: 'Plumbing — Fed balance sheet, reserves, money, credit impulse' },
  risk: { label: 'Risk', blurb: 'Stress — credit spreads, volatility, financial conditions, dollar' },
};
const AXIS_ORDER = ['growth', 'inflation', 'rates', 'liquidity', 'risk'];

const QUAD_TONE = {
  Goldilocks: { bg: 'rgba(16,185,129,0.14)', ring: UP, note: 'Growth firm · inflation cooling' },
  Reflation: { bg: 'rgba(245,158,11,0.14)', ring: AMBER, note: 'Growth firm · inflation rising' },
  Stagflation: { bg: 'rgba(239,68,68,0.14)', ring: DOWN, note: 'Growth soft · inflation sticky' },
  Deflation: { bg: 'rgba(59,130,246,0.16)', ring: '#3b82f6', note: 'Growth soft · inflation falling' },
};

// ── per-series history drawer (single-source-of-truth) ──────────────────────────────────────────────
const RANGES = [['1Y', 365], ['5Y', 1825], ['10Y', 3650], ['Max', 0]];
function rangeStart(days) {
  if (!days) return '';
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}
function SeriesDrawer({ node, onClose }) {
  const [range, setRange] = useState(3650);
  const [ser, setSer] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = isDark() ? 'dark' : 'light';
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => {
    if (!node) return undefined;
    let alive = true; setLoading(true);
    atlasService.getSeries(node.code, range ? { start: rangeStart(range) } : {})
      .then((r) => { if (alive) setSer(r.data); })
      .catch(() => { if (alive) setSer(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [node, range]);
  const obs = ser?.observations || [];
  const trace = useMemo(() => (obs.length ? [{
    x: obs.map((p) => p.date), y: obs.map((p) => (p.value == null ? null : p.value)),
    type: 'scatter', mode: 'lines', line: { color: GOLD, width: 1.6 }, connectgaps: false,
    hovertemplate: '%{x|%b %Y}: %{y:.2f}<extra></extra>',
  }] : null), [obs]);
  if (!node) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-white p-5 shadow-2xl dark:bg-[#0d1117]" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">{node.title}</h3>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <span>{node.code}</span>
              <span>·</span>
              <span>{node.cadence || node.unit}</span>
              {node.stale && <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-600 dark:text-amber-400">stale</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"><MdClose size={22} /></button>
        </div>

        {/* reality triple */}
        <div className="mb-4 grid grid-cols-3 gap-2">
          <Stat label="Latest" value={`${fmtVal(node.raw)}`} sub={node.unit} />
          <Stat label="Percentile · 10y" value={node.pctile == null ? '—' : `${fmtPct(node.pctile)}ᵗʰ`} sub="of history" />
          <Stat label="Robust z" value={fmtZ(node.z)} sub={node.stance} valueColor={tone(node.goodUp, node.z)} />
        </div>

        <div className="mb-2 flex items-center gap-1">
          {RANGES.map(([lab, d]) => (
            <button key={lab} onClick={() => setRange(d)}
              className={`rounded px-2 py-0.5 text-[11px] ${range === d ? 'bg-brand-gold/15 font-semibold text-brand-gold' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}>{lab}</button>
          ))}
        </div>
        <div className="min-h-[300px] rounded-lg border border-gray-200 p-1 dark:border-gray-800">
          {loading ? <div className="flex h-[300px] items-center justify-center text-sm text-gray-400">Loading…</div>
            : trace ? <Plot data={trace} layout={mergeLayout({ height: 300, margin: { l: 50, r: 12, t: 8, b: 30 }, showlegend: false }, theme)} config={PLOTLY_CONFIG} style={{ width: '100%' }} useResizeHandler />
              : <div className="flex h-[300px] items-center justify-center text-sm text-gray-400">No history</div>}
        </div>

        {/* enrichment — the single source of truth */}
        <div className="mt-4 space-y-3 text-sm">
          {node.definition && <Field label="What it is">{node.definition}</Field>}
          {node.means && <Field label="What a rise means">{node.means}</Field>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Transform">{node.transform === 'yoy' ? 'Year-over-year %' : 'Level'}</Field>
            <Field label="Polarity">{node.polarity === -1 ? '−1 (inverted for axis)' : '+1'}</Field>
            <Field label="Importance">{'★'.repeat(Math.round(node.importance || 1))}</Field>
            <Field label="Cadence">{node.cadence || '—'}</Field>
          </div>
          <a href={node.source_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12px] text-brand-gold hover:underline">
            FRED source <MdOpenInNew size={13} />
          </a>
        </div>
      </div>
    </div>
  );
}
const Stat = ({ label, value, sub, valueColor }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2 dark:border-gray-800 dark:bg-white/[0.02]">
    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    <div className="mt-0.5 font-serif text-lg tabular-nums" style={valueColor ? { color: valueColor } : undefined}>{value}</div>
    {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
  </div>
);
const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    <div className="mt-0.5 text-gray-700 dark:text-gray-300">{children}</div>
  </div>
);

// ── series table (sortable) ─────────────────────────────────────────────────────────────────────────
const SERIES_COLS = [
  { key: 'title', type: 'string', get: (r) => r.title },
  { key: 'raw', type: 'number', get: (r) => r.raw },
  { key: 'pctile', type: 'number', get: (r) => r.pctile },
  { key: 'z', type: 'number', get: (r) => r.z },
  { key: 'importance', type: 'number', get: (r) => r.importance },
];
function SeriesTable({ series, goodUp, onOpen }) {
  const rows = useMemo(() => series.map((s) => ({ ...s, goodUp })), [series, goodUp]);
  const { sorted, toggle, indicator } = useSortableRows(rows, SERIES_COLS, { key: 'z', dir: 'desc' });
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[560px] text-[12px]">
        <thead>
          <tr className="border-b border-gray-200 text-gray-500 dark:border-gray-800 dark:text-gray-400">
            <SortableTh label="Series" colKey="title" toggle={toggle} indicator={indicator} align="left" />
            <SortableTh label="Latest" colKey="raw" toggle={toggle} indicator={indicator} />
            <SortableTh label="%ile" colKey="pctile" toggle={toggle} indicator={indicator} />
            <SortableTh label="z" colKey="z" toggle={toggle} indicator={indicator} />
            <th className="px-2 py-1.5 text-left font-medium">Stance</th>
            <SortableTh label="Imp" colKey="importance" toggle={toggle} indicator={indicator} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((s) => (
            <tr key={s.code} onClick={() => onOpen(s)}
              className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-white/[0.03]">
              <td className="px-2 py-1.5">
                <div className="text-gray-800 dark:text-gray-200">{s.title}</div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">{s.code}{s.stale ? ' · stale' : ''}</div>
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                {fmtVal(s.raw)}<span className="ml-0.5 text-[9px] text-gray-400">{s.transform === 'yoy' ? '%y' : ''}</span>
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-500 dark:text-gray-400">{fmtPct(s.pctile)}</td>
              <td className="px-2 py-1.5 text-right font-semibold tabular-nums" style={{ color: tone(goodUp, s.z) }}>{fmtZ(s.z)}</td>
              <td className="px-2 py-1.5 text-[11px]" style={{ color: tone(goodUp, s.z) }}>{s.stance}</td>
              <td className="px-2 py-1.5 text-right text-[10px] text-amber-500">{'★'.repeat(Math.round(s.importance || 1))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── group block (collapsible; weight + z + contribution) ─────────────────────────────────────────────
function GroupBlock({ name, group, goodUp, onOpen }) {
  const [open, setOpen] = useState(false);
  const t = tone(goodUp, group.z);
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 px-3 py-2 text-left">
        {open ? <MdExpandMore size={16} className="text-gray-400" /> : <MdChevronRight size={16} className="text-gray-400" />}
        <div className="flex-1">
          <div className="text-[13px] font-medium text-gray-800 dark:text-gray-200">{name}</div>
          <div className="text-[10px] uppercase tracking-wide text-gray-400">
            weight {Math.round((group.weight || 0) * 100)}% · {group.series.length} series
          </div>
        </div>
        <div className="text-right">
          <div className="font-serif text-base tabular-nums" style={{ color: t }}>{fmtZ(group.z)}</div>
          <div className="text-[10px]" style={{ color: t }}>{group.stance}</div>
        </div>
        {/* contribution weight bar */}
        <div className="ml-1 hidden w-24 sm:block">
          <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700">
            <div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, (group.weight || 0) * 100)}%`, background: GOLD }} />
          </div>
        </div>
      </button>
      {open && <div className="border-t border-gray-100 px-1 py-1 dark:border-gray-800/60"><SeriesTable series={group.series} goodUp={goodUp} onOpen={onOpen} /></div>}
    </div>
  );
}

// ── axis card (collapsible; headline stance + drill to groups) ───────────────────────────────────────
// Expanded-card trajectory: the axis level (importance-weighted robust z) month by month, read
// straight from payload.timeline — no extra API call. Older stored snapshots only carried
// growth/inflation in the timeline; other axes show the accrual note until the next recompute.
const TRAJ_RANGES = [['1Y', 12], ['3Y', 36], ['5Y', 60], ['10Y', 120], ['Max', null]];
// COVID shock + the YoY base-effect echo it drags through the following year. Excluded months
// are dropped from the window percentile AND blanked on the line (which also rescales the
// y-axis, so non-COVID variation stops looking flat next to the ±8σ spikes).
const COVID_FROM = '2020-02'; const COVID_TO = '2021-12';
const inCovid = (d) => { const ym = String(d).slice(0, 7); return ym >= COVID_FROM && ym <= COVID_TO; };
function AxisTrajectory({ axis, timeline, goodUp }) {
  const theme = isDark() ? 'dark' : 'light';
  const [range, setRange] = useState('Max');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [exCovid, setExCovid] = useState(false);
  const pts = (timeline || []).filter((p) => p[axis] != null);
  if (pts.length < 2) {
    return <div className="px-1 py-2 text-[11px] text-gray-400">Trajectory for this axis appears after the next regime recompute (hit Recompute above).</div>;
  }
  let shown = pts;
  if (range === 'Custom') {
    shown = pts.filter((p) => {
      const ym = String(p.date).slice(0, 7);
      return (!custom.from || ym >= custom.from) && (!custom.to || ym <= custom.to);
    });
  } else {
    const months = (TRAJ_RANGES.find(([lbl]) => lbl === range) || [])[1];
    if (months) shown = pts.slice(-months);
  }
  // ex-COVID mode prefers the backend-renormalized series ({axis}_xc: COVID months dropped from
  // the rolling median/MAD estimation sample). Payloads stored before that variant existed fall
  // back to a display-only exclusion of the canonical values.
  const hasXc = exCovid && pts.some((p) => p[axis + '_xc'] != null);
  const val = (p) => (hasXc ? p[axis + '_xc'] : p[axis]);
  // stats sample = the selected window, minus COVID months when toggled
  const usable = (exCovid ? shown.filter((p) => !inCovid(p.date)) : shown).filter((p) => val(p) != null);
  const last = usable[usable.length - 1];
  // same method as the backend 10y read (macro_regime._pctile): share of window strictly below
  // the latest value; needs >=24 months to mean anything.
  const winPct = (usable.length >= 24 && last)
    ? Math.round((usable.filter((p) => val(p) < val(last)).length / usable.length) * 100)
    : null;
  const trace = (usable.length < 2 || !last) ? null : [{
    x: shown.map((p) => p.date),
    y: shown.map((p) => (exCovid && inCovid(p.date) ? null : val(p))),
    type: 'scatter', mode: 'lines',
    line: { color: GOLD, width: 1.6 },
    hovertemplate: '%{x|%b %Y} · %{y:+.2f}<extra></extra>',
  }, {
    x: [last.date], y: [val(last)], type: 'scatter', mode: 'markers',
    marker: { size: 8, color: tone(goodUp, val(last)) }, hoverinfo: 'skip',
  }];
  const layout = mergeLayout({
    height: 170, margin: { l: 34, r: 10, t: 6, b: 26 }, showlegend: false,
    yaxis: { zeroline: true, zerolinecolor: 'rgba(148,163,184,0.4)' },
  }, theme);
  const pillCls = (active) => `rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide transition-colors ${active ? 'bg-[#c9a96e] text-white' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-1 px-1 pb-1">
        <div className="text-[10px] uppercase tracking-wide text-gray-400">
          Axis level · {usable.length} months
          {winPct != null && (
            <span> · pctile <span className="font-semibold text-gray-600 dark:text-gray-300">{winPct}ᵗʰ</span> in {range === 'Custom' ? 'window' : range.toLowerCase()}{exCovid ? (hasXc ? ' ex-COVID (renormalized)' : ' ex-COVID (view only)') : ''}</span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          {TRAJ_RANGES.map(([lbl]) => (
            <button key={lbl} onClick={(e) => { e.stopPropagation(); setRange(lbl); }} className={pillCls(range === lbl)}>{lbl}</button>
          ))}
          <button onClick={(e) => { e.stopPropagation(); setRange('Custom'); }} className={pillCls(range === 'Custom')}>Custom</button>
          <span className="mx-0.5 h-3 w-px bg-gray-200 dark:bg-gray-700" />
          <button onClick={(e) => { e.stopPropagation(); setExCovid((v) => !v); }} className={pillCls(exCovid)} title={`Drops ${COVID_FROM} → ${COVID_TO} from the line and the window percentile`}>ex-COVID</button>
        </div>
      </div>
      {range === 'Custom' && (
        <div className="flex items-center gap-2 px-1 pb-1 text-[11px] text-gray-400" onClick={(e) => e.stopPropagation()}>
          <input type="month" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
            className="rounded border border-gray-200 bg-transparent px-1 py-0.5 text-[11px] text-gray-600 dark:border-gray-700 dark:text-gray-300" />
          <span>→</span>
          <input type="month" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
            className="rounded border border-gray-200 bg-transparent px-1 py-0.5 text-[11px] text-gray-600 dark:border-gray-700 dark:text-gray-300" />
        </div>
      )}
      {trace
        ? <Plot data={trace} layout={layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} useResizeHandler />
        : <div className="px-1 py-3 text-[11px] text-gray-400">No months in the selected window — stored data runs {String(pts[0].date).slice(0, 7)} → {String(pts[pts.length - 1].date).slice(0, 7)}.</div>}
    </div>
  );
}

function AxisCard({ axis, data, timeline, onOpen }) {
  const [open, setOpen] = useState(false);
  const meta = AXIS_META[axis];
  const t = tone(data.good_up, data.level);
  const momT = tone(data.good_up, data.mom);
  const groups = Object.entries(data.groups || {}).sort((a, b) => (b[1].weight || 0) - (a[1].weight || 0));
  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0d1117]">
      <button onClick={() => setOpen((v) => !v)} className="w-full p-4 text-left">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">{meta.label}</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-serif text-3xl tabular-nums" style={{ color: t }}>{fmtZ(data.level)}</span>
              <span className="text-sm font-medium" style={{ color: t }}>{data.stance}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 text-[11px] text-gray-400">
            {data.mom != null && Math.abs(data.mom) >= 0.1 && (
              <span className="inline-flex items-center" style={{ color: momT }}>
                {data.mom > 0 ? <MdArrowUpward size={13} /> : <MdArrowDownward size={13} />}{fmtZ(data.mom)}
              </span>
            )}
            {open ? <MdExpandMore size={18} /> : <MdChevronRight size={18} />}
          </div>
        </div>
        {/* percentile + breadth rail */}
        <div className="mt-3 space-y-1.5">
          <Rail label="Percentile · 10y" pct={data.pctile} color={t} suffix="ᵗʰ" />
          <Rail label="Breadth · % rising" pct={data.breadth} color={GOLD} suffix="%" />
        </div>
        <div className="mt-2 text-[11px] text-gray-400">{meta.blurb}</div>
      </button>
      {open && (
        <div className="space-y-2 border-t border-gray-100 p-3 dark:border-gray-800/60">
          <AxisTrajectory axis={axis} timeline={timeline} goodUp={data.good_up} />
          {groups.map(([g, gd]) => <GroupBlock key={g} name={g} group={gd} goodUp={data.good_up} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
}
const Rail = ({ label, pct, color, suffix }) => (
  <div className="flex items-center gap-2">
    <span className="w-28 shrink-0 text-[10px] uppercase tracking-wide text-gray-400">{label}</span>
    <div className="h-1.5 flex-1 rounded-full bg-gray-200 dark:bg-gray-700">
      <div className="h-1.5 rounded-full" style={{ width: `${pct == null ? 0 : Math.max(2, Math.min(100, pct))}%`, background: color }} />
    </div>
    <span className="w-9 shrink-0 text-right text-[11px] tabular-nums text-gray-500 dark:text-gray-400">{pct == null ? '—' : `${Math.round(pct)}${suffix}`}</span>
  </div>
);

// ── quadrant hero + trajectory ───────────────────────────────────────────────────────────────────────
function QuadrantHero({ payload }) {
  const theme = isDark() ? 'dark' : 'light';
  const g = payload.axes?.growth?.level;
  const i = payload.axes?.inflation?.level;
  const quad = payload.quadrant;
  const tl = payload.timeline || [];
  // trajectory of the last 24 months in growth×inflation space
  const path = tl.slice(-24).filter((p) => p.growth != null && p.inflation != null);
  const trace = path.length ? [{
    x: path.map((p) => p.growth), y: path.map((p) => p.inflation),
    type: 'scatter', mode: 'lines+markers',
    line: { color: 'rgba(148,163,184,0.5)', width: 1.4 },
    marker: { size: path.map((_, idx) => (idx === path.length - 1 ? 12 : 4)), color: path.map((_, idx) => (idx === path.length - 1 ? GOLD : 'rgba(148,163,184,0.6)')) },
    hovertemplate: 'G %{x:+.2f} · I %{y:+.2f}<extra></extra>',
  }] : null;
  const layout = mergeLayout({
    height: 300, margin: { l: 40, r: 12, t: 8, b: 34 }, showlegend: false,
    xaxis: { title: { text: 'Growth →', font: { size: 10 } }, zeroline: true, zerolinecolor: 'rgba(148,163,184,0.4)', range: [-2.2, 2.2] },
    yaxis: { title: { text: 'Inflation →', font: { size: 10 } }, zeroline: true, zerolinecolor: 'rgba(148,163,184,0.4)', range: [-2.2, 2.2] },
    shapes: [
      { type: 'rect', x0: 0, x1: 2.2, y0: -2.2, y1: 0, fillcolor: QUAD_TONE.Goldilocks.bg, line: { width: 0 }, layer: 'below' },
      { type: 'rect', x0: 0, x1: 2.2, y0: 0, y1: 2.2, fillcolor: QUAD_TONE.Reflation.bg, line: { width: 0 }, layer: 'below' },
      { type: 'rect', x0: -2.2, x1: 0, y0: 0, y1: 2.2, fillcolor: QUAD_TONE.Stagflation.bg, line: { width: 0 }, layer: 'below' },
      { type: 'rect', x0: -2.2, x1: 0, y0: -2.2, y1: 0, fillcolor: QUAD_TONE.Deflation.bg, line: { width: 0 }, layer: 'below' },
    ],
    annotations: [
      { x: 1.1, y: -1.1, text: 'Goldilocks', showarrow: false, font: { size: 10, color: '#94a3b8' } },
      { x: 1.1, y: 1.1, text: 'Reflation', showarrow: false, font: { size: 10, color: '#94a3b8' } },
      { x: -1.1, y: 1.1, text: 'Stagflation', showarrow: false, font: { size: 10, color: '#94a3b8' } },
      { x: -1.1, y: -1.1, text: 'Deflation', showarrow: false, font: { size: 10, color: '#94a3b8' } },
    ],
  }, theme);
  const qt = QUAD_TONE[quad] || { ring: GOLD, note: '' };
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
      <div className="flex flex-col justify-center rounded-xl border p-5" style={{ borderColor: qt.ring, background: qt.bg }}>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Current regime</div>
        <div className="mt-1 font-serif text-4xl text-gray-900 dark:text-gray-100">{quad}</div>
        <div className="mt-1 text-[12px] text-gray-500 dark:text-gray-400">{qt.note}</div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Stat label="Growth" value={fmtZ(g)} valueColor={tone(true, g)} />
          <Stat label="Inflation" value={fmtZ(i)} valueColor={tone(false, i)} />
        </div>
      </div>
      <div className="rounded-xl border border-gray-200 p-2 dark:border-gray-800">
        <div className="px-2 pt-1 text-[10px] uppercase tracking-wide text-gray-400">24-month trajectory · growth × inflation</div>
        {trace ? <Plot data={trace} layout={layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} useResizeHandler />
          : <div className="flex h-[300px] items-center justify-center text-sm text-gray-400">No trajectory</div>}
      </div>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────────────────────────────
export default function MacroRegimePage() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);
  const [drawer, setDrawer] = useState(null);

  const load = useCallback((refresh) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    atlasService.getRegime(refresh ? { refresh: 1 } : {})
      .then((r) => { setPayload(r.data); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.error || 'Failed to load regime'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);
  useEffect(() => { load(false); }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-serif text-2xl text-gray-900 dark:text-gray-100">Macro Regime</h2>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            5-axis reality-anchored classifier · point-in-time robust z over the 299-series warehouse
            {/* as_of_date = the day this snapshot was computed/stored. payload.date is the MONTHLY
                period label (pandas stamps the in-progress month at its month-END, e.g. 09-30 on
                09-25) — show it as the data month, never as "as of". */}
            {payload?.as_of_date ? ` · as of ${payload.as_of_date}` : ''}
            {payload?.date ? ` · data month ${String(payload.date).slice(0, 7)} (in progress)` : ''}
          </p>
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-[12px] text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
          <MdRefresh size={15} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Recomputing…' : 'Recompute'}
        </button>
      </div>

      {err && <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400">{err}</div>}

      {loading ? (
        <div className="space-y-4">
          <div className="h-[300px] skeleton rounded-xl" />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-[220px] skeleton rounded-xl" />)}
          </div>
        </div>
      ) : payload ? (
        <>
          <QuadrantHero payload={payload} />
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Axes · drill to source</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {AXIS_ORDER.filter((a) => payload.axes?.[a]).map((a) => (
                <AxisCard key={a} axis={a} data={payload.axes[a]} timeline={payload.timeline} onOpen={setDrawer} />
              ))}
            </div>
          </div>
          <p className="text-[10px] leading-relaxed text-gray-400">
            Colour follows economic meaning, not the sign of z: growth &amp; liquidity are green when rising, inflation / rates / risk
            are red when rising. Within a concept group, series are combined by an importance-weighted mean of their robust z; groups
            combine into an axis by researched weights (CFNAI / CEI, Estrella-Mishkin, Gilchrist-Zakrajšek, Fed Core-PCE). Click any
            series for its full history + definition.
          </p>
        </>
      ) : null}

      {drawer && <SeriesDrawer node={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}
